import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { Line } from "react-chartjs-2";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useDashboardData } from './DashboardProvider';
import useTranslation from '../customHooks/translations';

const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"]
const ImpactBox = () => {
  const { impactObject:impact } = useDashboardData();
  const dateEnd = new Date().getMonth();

  // Only use months that exist in the current year's data (do not wrap into prior year)
  const monthsAvailable = Math.min(5, dateEnd + 1);
  const dateStart = dateEnd - monthsAvailable + 1;

  const graphData = [];
  const graphLabels = [];
  for (let i = 0; i < monthsAvailable; i++) {
    const index = dateStart + i;
    graphLabels.push(months[index]);
    graphData.push(impact?.actions_by_month?.[index] ?? 0);
  }

  // Compute week-over-week change from actual data when possible
  const thisWeek = impact?.weekly_co2 ?? 0;
  const lastWeek = impact?.previous_weekly_co2;
  let weekChangeLabel = null;
  if (lastWeek != null && lastWeek > 0) {
    const pct = Math.round(((thisWeek - lastWeek) / lastWeek) * 100);
    weekChangeLabel = `${pct >= 0 ? '+' : ''}${pct}%`;
  } else if (thisWeek > 0 && (lastWeek === 0 || lastWeek == null)) {
    weekChangeLabel = null; // no meaningful comparison
  }

  const translation = useTranslation();

  function formatNumber(num) {
    if (num == null || isNaN(num)) return '0 g';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(0) + ' tonnes';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(0) + ' kg';
    } else {
      return num.toFixed(0) + ' g';
    }
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 3,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: { size: 13, family: 'DM Sans' },
          color: '#64748B',
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
        },
      },
      title: {
        padding: 20,
        display: true,
        align: 'start',
        text: 'Total CO2 in grams',
        font: { size: 16, weight: '600', family: 'Outfit' },
        color: '#0F172A',
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#64748B',
          font: { size: 12, family: 'DM Sans' },
        },
        grid: {
          color: 'transparent',
        },
      },
      y: {
        ticks: {
          color: '#64748B',
          font: { size: 12, family: 'DM Sans' },
        },
        grid: {
          color: '#F1F5F9',
        },
      },
    },
    elements: {
      line: {
        tension: 0.4,
        borderWidth: 3,
      },
      point: {
        radius: 4,
        hoverRadius: 6,
        backgroundColor: '#FFFFFF',
        borderWidth: 3,
      },
    },
  };

  const data = {
    labels: graphLabels,
    datasets: [
      {
        label: "My progress",
        data: graphData,
        fill: true,
        borderColor: '#0D9488',
        backgroundColor: 'rgba(13,148,136,0.08)',
        pointBorderColor: '#0D9488',
      },
    ],
  };

  return (
    <Box>
      <Paper sx={{
        borderRadius: '14px',
        background: 'linear-gradient(135deg, #EA580C 0%, #F97316 100%)',
        padding: '1.5em 1em',
        textAlign: 'center',
        color: '#FFFFFF',
        lineHeight: '1.2em',
        mb: '1.5em',
        border: 'none',
        boxShadow: '0 4px 16px rgba(234,88,12,0.2)',
      }}>
        <Typography sx={{
          fontWeight: 800,
          paddingBottom: '8px',
          fontSize: '1.3em',
          color: '#FFFFFF',
          fontFamily: '"Outfit", "DM Sans", sans-serif',
        }}>
          {translation.formatString(translation['dashboardPage'].co2Saved, formatNumber(impact.weekly_co2))}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
          {translation['dashboardPage'].thisWeek}
          {weekChangeLabel && (
            <>
              <br/>
              <TrendingUpIcon sx={{color: '#FED7AA', marginBottom: '-4px', fontSize: '1.1rem' }}/>{' '}
              <strong>{weekChangeLabel}</strong> {translation['dashboardPage'].fromLastWeek}
            </>
          )}
        </Typography>
      </Paper>
      <div style={{ height: '400px' }}>
        <Line options={options} data={data} aria-label="Line chart showing the impact of your actions over time"/>
      </div>
    </Box>
  )
};

export default ImpactBox;
